import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navLinks = [
  { to: "/", label: "首页", end: true },
  { to: "/channels", label: "频道分类", end: false },
  { to: "/community", label: "互动社区", end: false },
  { to: "/about", label: "关于我们", end: false },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) {
      searchRef.current?.focus();
    }
  }, [searchOpen]);

  const submitSearch = (value: string) => {
    const query = value.trim();
    if (!query) {
      return;
    }
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setSearchOpen(false);
    setDrawerOpen(false);
  };

  const onSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitSearch(keyword);
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap text-sm font-medium transition-colors ${
      isActive ? "text-primary-300" : "text-foreground-600 hover:text-foreground-950"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/60 bg-background-50/90 backdrop-blur-md">
      <div className="flex h-14 w-full items-center gap-3 px-4 md:h-16 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50 md:h-9 md:w-9">
            <i className="ri-live-line text-lg md:text-xl" />
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">
            360<span className="text-primary-400">直播</span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-7 md:flex">
          {navLinks.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.end} className={navClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <form
            onSubmit={onSearchSubmit}
            className="hidden items-center md:flex"
          >
            <div
              className={`flex items-center gap-2 overflow-hidden rounded-full border border-background-200 bg-background-100 transition-all duration-300 ${
                searchOpen ? "w-56 px-3" : "w-9 px-0"
              }`}
            >
              <button
                type={searchOpen ? "submit" : "button"}
                aria-label="搜索直播"
                onClick={() => {
                  if (!searchOpen) {
                    setSearchOpen(true);
                  }
                }}
                className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center text-foreground-500 transition-colors hover:text-primary-300"
              >
                <i className="ri-search-line text-lg" />
              </button>
              <input
                ref={searchRef}
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索直播或主播"
                className={`w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none ${
                  searchOpen ? "block" : "hidden"
                }`}
              />
            </div>
          </form>

          <Link
            to="/channels"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            开始观看
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setDrawerOpen(true)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-900 transition-colors hover:bg-background-100 md:hidden"
          >
            <i className="ri-menu-line text-xl" />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] md:hidden ${drawerOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!drawerOpen}
      >
        <div
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 bg-background-50/70 backdrop-blur-sm transition-opacity duration-300 ${
            drawerOpen ? "opacity-100" : "opacity-0"
          }`}
        />
        <aside
          className={`absolute left-0 top-0 h-full w-[78%] max-w-xs border-r border-background-200/60 bg-background-100 p-5 transition-transform duration-300 ${
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="mb-6 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-live-line text-lg" />
              </span>
              <span className="font-heading text-base font-extrabold text-foreground-950">
                360<span className="text-primary-400">直播</span>
              </span>
            </span>
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setDrawerOpen(false)}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-200"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <form onSubmit={onSearchSubmit} className="mb-5">
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索直播或主播"
                className="h-9 w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
              />
            </div>
          </form>

          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                onClick={() => setDrawerOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-500/15 text-primary-300"
                      : "text-foreground-700 hover:bg-background-200"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/channels"
            onClick={() => setDrawerOpen(false)}
            className="mt-6 flex items-center justify-center rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
          >
            开始观看
          </Link>
        </aside>
      </div>
    </header>
  );
}